import { useEffect, useRef, useState } from 'react'

import {
  Alert,
  Group,
  Loader,
  Menu,
  Modal,
  SegmentedControl,
  Textarea,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useMutation } from '@redwoodjs/web'
import { useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import {
  findUnresolvedTokens,
  HighlightedPreviewText,
  PreviewUnavailable,
  UnresolvedVariablesNotice,
} from 'src/components/DataDisplay/TemplatePreview/TemplatePreview'
import CustomFieldVariableMenu from 'src/components/Overlays/CustomFieldVariableMenu/CustomFieldVariableMenu'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_CUSTOM_FIELDS } from 'src/graphql/customFields.graphql'
import {
  CREATE_TEXT_TEMPLATE,
  EDIT_TEXT_TEMPLATE,
  PREVIEW_TEMPLATE_CONTENT,
} from 'src/graphql/textTemplates.graphql'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import {
  SMS_SPAM_WARNING,
  SMS_TEMPLATE_HARD_LIMIT,
  SMS_TEMPLATE_HARD_LIMIT_MESSAGE,
  SMS_TEMPLATE_WARNING_THRESHOLD,
} from 'src/lib/sms'
import { focusCaretAfterInsert, insertAtCaret } from 'src/lib/textInput.utils'
import { TextInputMentions } from 'src/lib/textInputMentions'
import { SMS_TEMPLATE_VARIABLES } from 'src/lib/textTemplateVariables'

import { getEstimatedLength } from './getEstimatedLength'

const VIEW = { EDIT: 'edit', PREVIEW: 'preview' } as const

const CreateTemplateModal = ({
  textTemplate,
  isOpen,
  refetchQueries,
  onClose,
  edit = false,
}) => {
  const [cursorPosition, setCursorPosition] = useState(0)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const skipOpenRef = useRef(false)
  const [mentionOpen, setMentionOpen] = useState(false)
  const [selectedWord, setSelectedWord] = useState('')
  const [view, setView] = useState<string>(VIEW.EDIT)

  //FORM SETUP:
  const schema = z.object({
    title: z
      .string()
      .min(1, { message: 'Please include a title' })
      .max(100, { message: 'Template title is too long' }),
    content: z
      .string()
      .min(1, { message: 'Please include a text template' })
      .max(SMS_TEMPLATE_HARD_LIMIT, SMS_TEMPLATE_HARD_LIMIT_MESSAGE)
      .refine((val) => getEstimatedLength(val) <= SMS_TEMPLATE_HARD_LIMIT, {
        message: SMS_TEMPLATE_HARD_LIMIT_MESSAGE,
      }),
  })

  const form = useForm({
    initialValues: {
      title: textTemplate?.title || '',
      content: textTemplate?.content || '',
    },
    validate: zodResolver(schema),
  })

  useEffect(() => {
    form.setValues({
      title: textTemplate?.title || '',
      content: textTemplate?.content || '',
    })
    setView(VIEW.EDIT)
  }, [textTemplate, isOpen])

  const content = String(form.values.content ?? '')
  const canPreview = !!content.trim()
  const isPreview = view === VIEW.PREVIEW && canPreview
  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = useQuery(
    PREVIEW_TEMPLATE_CONTENT,
    { variables: { content }, skip: !isPreview }
  )
  const previewContent = previewData?.previewTemplateContent?.content ?? ''

  //MUTATIONS:
  const [editTextTemplate] = useMutation(EDIT_TEXT_TEMPLATE, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      toast('Your text template was updated successfully', 'success')
      onClose()
    },
    onError: () => {
      toast('Unable to update text template, please try again.', 'error')
    },
  })

  const [createTextTemplate] = useMutation(CREATE_TEXT_TEMPLATE, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      toast('A new text template was created successfully', 'success')
      onClose()
    },
    onError: () => {
      toast('Unable to create text template, please try again.', 'error')
    },
  })

  // Load active custom fields to offer as variables
  const { data: customFieldsData } = useQuery(GET_CUSTOM_FIELDS, {
    variables: { status: 'ACTIVE', pageInfo: { limit: 200, offset: 0 } },
  })
  const customFieldVariables =
    customFieldsData?.customFields?.items?.map((f) => ({
      name: f.label,
      value: `cf:${f.key}`,
      group: 'Custom field',
    })) ?? []
  const standardVariables = SMS_TEMPLATE_VARIABLES.map((v) => ({
    name: v.name,
    value: v.value,
    group: 'Standard',
  }))
  const allVariables = [...standardVariables, ...customFieldVariables]

  // @ variable picker open/close based on the word under the caret (shared rule)
  useEffect(() => {
    if (skipOpenRef.current) {
      skipOpenRef.current = false
      setMentionOpen(false)
      return
    }
    const query = TextInputMentions.getTriggerQuery(
      String(form.values.content ?? ''),
      Math.max(0, Math.min(cursorPosition, String(form.values.content ?? '').length))
    )
    if (query !== null) {
      setSelectedWord(query)
      setMentionOpen(true)
    } else {
      setMentionOpen(false)
    }
  }, [form.values.content, cursorPosition])

  const insertVariableAtCaret = (rawValue: string) => {
    setView(VIEW.EDIT)
    const token = `{{${rawValue}}}`
    const { content, caret } = insertAtCaret(inputRef.current, String(form.values.content ?? ''), token)
    skipOpenRef.current = true
    form.setFieldValue('content', content)
    setCursorPosition(caret)
    focusCaretAfterInsert(inputRef.current, caret)
  }

  const onFormSubmit = (values) => {
    edit
      ? editTextTemplate({
          variables: {
            textTemplateId: textTemplate.textTemplateId,
            input: {
              ...values,
            },
          },
        })
      : createTextTemplate({
          variables: {
            input: {
              ...values,
            },
          },
        })
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      size={'lg'}
      classNames={{ title: 'text-lg font-bold' }}
      title={edit ? 'Edit SMS Template' : 'Create SMS Template'}
      centered
    >
      <div className="flex flex-col gap-6">
        <form
          onSubmit={form.onSubmit(
            (values) => onFormSubmit(values),
            () => setView(VIEW.EDIT)
          )}
        >
          <div className="flex flex-col gap-6">
            <TextInput
              placeholder={
                form.values.title
                  ? (form.values.title as string)
                  : 'SMS Template # 1'
              }
              label="Template Name"
              {...form.getInputProps('title')}
            />
            <div className="flex flex-col gap-3">
              <div className="text-sm font-medium text-doubleNickel-gray-900">
                Click on the variable to insert it in the template
              </div>
              <div className="flex flex-row flex-wrap gap-2">
                {SMS_TEMPLATE_VARIABLES.map((variable) => (
                  <StyledBadge
                    key={variable.value}
                    textTransform="capitalize"
                    onClick={() => {
                      skipOpenRef.current = true
                      //This sets the variable at the selected cursor position
                      insertVariableAtCaret(variable.value)
                    }}
                  >
                    {variable.name}
                  </StyledBadge>
                ))}
                <CustomFieldVariableMenu
                  variables={customFieldVariables}
                  onSelect={(value) => {
                    skipOpenRef.current = true
                    insertVariableAtCaret(value)
                  }}
                  renderTarget={(label) => (
                    <StyledBadge textTransform="capitalize">
                      {label}
                    </StyledBadge>
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-medium text-doubleNickel-gray-900">
                  Message
                </div>
                <SegmentedControl
                  size="xs"
                  value={isPreview ? VIEW.PREVIEW : VIEW.EDIT}
                  onChange={setView}
                  data={[
                    { label: 'Edit', value: VIEW.EDIT },
                    {
                      label: 'Preview',
                      value: VIEW.PREVIEW,
                      disabled: !canPreview,
                    },
                  ]}
                />
              </div>
              {isPreview && (
                <div className="h-36 cursor-default select-text overflow-y-auto rounded border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-3 py-2 text-sm text-doubleNickel-gray-700">
                  {previewLoading ? (
                    <div className="flex h-full items-center justify-center">
                      <Loader size="xs" color="gray" />
                    </div>
                  ) : previewError ? (
                    <PreviewUnavailable onRetry={() => refetchPreview()} />
                  ) : (
                    <HighlightedPreviewText text={previewContent} />
                  )}
                </div>
              )}
              <div className={isPreview ? 'hidden' : undefined}>
                <StyledMenu opened={mentionOpen && !isPreview}>
                  <Menu.Target>
                    <Textarea
                      ref={inputRef}
                      maxRows={6}
                      classNames={{
                        input: 'h-36',
                      }}
                      placeholder={
                        form.values.content
                          ? (form.values.content as string)
                          : 'Write here...'
                      }
                      value={form.values.content as string}
                      error={form.errors['content']}
                      onClick={(e) => {
                        // Track caret for correct insertion point
                        setCursorPosition(
                          (e.target as HTMLTextAreaElement).selectionStart ?? 0
                        )
                      }}
                      onChange={(e) => {
                        form.setFieldValue('content', e.target.value)
                        setCursorPosition(
                          (e.target as HTMLTextAreaElement).selectionStart ?? 0
                        )
                      }}
                    />
                  </Menu.Target>
                  <Menu.Dropdown
                    className="max-h-[300px] overflow-y-auto"
                    onFocus={() => inputRef.current?.focus()}
                  >
                    {allVariables
                      .filter((v) =>
                        v.name
                          .toLowerCase()
                          .includes(selectedWord.toLowerCase())
                      )
                      .map((v) => (
                        <Menu.Item
                          key={v.group + ':' + v.value}
                          onClick={() => {
                            const token = `{{${v.value}}}`
                            const { content, caret } = TextInputMentions.replaceAtTrigger(
                              inputRef.current,
                              String(form.values.content ?? ''),
                              token
                            )
                            skipOpenRef.current = true
                            form.setFieldValue('content', content)
                            setCursorPosition(caret)
                            focusCaretAfterInsert(inputRef.current, caret)
                            setMentionOpen(false)
                          }}
                          className="text-xs"
                        >
                          {v.name}
                        </Menu.Item>
                      ))}
                  </Menu.Dropdown>
                </StyledMenu>
              </div>
              <div className="text-xs font-medium text-doubleNickel-gray-600">
                {isPreview ? (
                  'Shown with a sample applicant. Company and recruiter details are real.'
                ) : (
                  <>
                    ~{getEstimatedLength(form.values.content || '')}/
                    {SMS_TEMPLATE_HARD_LIMIT} estimated
                  </>
                )}
              </div>
              {isPreview && !previewLoading && !previewError && (
                <UnresolvedVariablesNotice
                  tokens={findUnresolvedTokens(previewContent)}
                />
              )}
              {!isPreview &&
                getEstimatedLength(form.values.content || '') >
                  SMS_TEMPLATE_WARNING_THRESHOLD && (
                  <Alert
                    icon={
                      <IconAlertTriangle className="h-4 w-4 fill-none stroke-doubleNickel-warning-500" />
                    }
                    color="yellow"
                    variant="light"
                    classNames={{
                      root: 'py-2',
                      body: 'gap-0',
                      message: 'text-xs',
                    }}
                  >
                    {SMS_SPAM_WARNING}
                  </Alert>
                )}
            </div>
          </div>
          <Group
            justify="flex-end"
            mt="md"
            className="grid grid-cols-2 gap-4 border-t py-4"
          >
            <Button text="Cancel" variant="outline" onClick={onClose} />
            <Button
              type="submit"
              text={edit ? 'Save' : 'Create'}
              variant="filled"
            />
          </Group>
        </form>
      </div>
    </Modal>
  )
}

export default CreateTemplateModal
